import FilterIcon from "@/assets/icons/filter.svg?react";
import { toTitleCase } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import {
  hasFilterValue,
  canApplyNumericFilter,
  getCategoricalFilterMode,
  getCategoricalFilterValues,
  getInitialNumericValues,
  getInitialDateValues,
} from "./filtersUtil";
import type {
  CategoricalFilterMode,
  FilterType,
  FilterValue,
  FilterConditionValue,
  NumericCondition,
  DateApplyData,
} from "./filterTypes";
import FilterConditionSelector from "./FilterConditionSelector";
import FilterCalendar from "./FilterCalendar";
import FilterOptions, { type Option } from "./FilterOptions";

interface FilterPanelProps {
  activeFilter: string | null;
  currentDataType: FilterType;
  selectedFilters: Record<string, FilterConditionValue>;
  tempSelectedFilters: Record<string, FilterConditionValue>;
  currentFilterOptions: Option[];
  handleClose: () => void;
  handleClear: () => void;
  handleApplyNumericFilter: (
    filterKey: string,
    condition: NumericCondition | null,
    value: number | { min: number | null; max: number | null },
  ) => void;
  handleApplyDateFilter: (filterKey: string, filterData: DateApplyData) => void;
  handleApplyFilter: () => void;
  handleTempSelectionChange: (
    filterKey: string,
    values: FilterValue[],
    mode?: CategoricalFilterMode,
  ) => void;
  columnsTitleCreator?: ((key: string) => string) | null;
}

export default function FilterPanel({
  activeFilter,
  currentDataType,
  selectedFilters,
  tempSelectedFilters,
  currentFilterOptions,
  handleClose,
  handleClear,
  handleApplyNumericFilter,
  handleApplyDateFilter,
  handleApplyFilter,
  handleTempSelectionChange,
  columnsTitleCreator,
}: FilterPanelProps) {
  const getCategoricalFilterState = (
    filterKey: string,
  ): { values: FilterValue[]; mode: CategoricalFilterMode } => {
    const filterValue =
      tempSelectedFilters[filterKey] ?? selectedFilters[filterKey];

    return {
      values: getCategoricalFilterValues(filterValue),
      mode: getCategoricalFilterMode(filterValue),
    };
  };

  const renderFilterComponent = (filterKey: string, dataType: FilterType) => {
    const commonProps = {
      onClose: handleClose,
      onClear: handleClear,
    };

    const isCurrentFilterActive = hasFilterValue(selectedFilters[filterKey]);

    switch (dataType) {
      case "numeric": {
        const filterValue =
          tempSelectedFilters[filterKey] || selectedFilters[filterKey];
        const { condition, value, min, max } =
          getInitialNumericValues(filterValue);
        return (
          <FilterConditionSelector
            key={filterKey}
            condition={condition}
            value={value}
            minValue={min}
            maxValue={max}
            onConditionChange={() => {
              // console.log("Condition changed:", newCondition)
            }}
            onValueChange={() => {
              // console.log("Value changed:", newValue)
            }}
            onApply={(finalCondition, finalValue) => {
              if (
                canApplyNumericFilter(
                  finalCondition,
                  finalValue as number | { min: number; max: number } | null,
                )
              ) {
                handleApplyNumericFilter(filterKey, finalCondition, finalValue);
              }
            }}
            initialCondition={condition}
            initialValue={value}
            isActiveFilter={isCurrentFilterActive}
            {...commonProps}
          />
        );
      }

      case "datetime": {
        const filterValue =
          tempSelectedFilters[filterKey] || selectedFilters[filterKey];
        const { condition, date, range, mode, relativePreset } =
          getInitialDateValues(filterValue);
        return (
          <FilterCalendar
            key={filterKey}
            onApply={(filterData) =>
              handleApplyDateFilter(filterKey, filterData)
            }
            initialCondition={condition}
            initialDate={date}
            initialRange={range}
            initialMode={mode}
            initialRelativePreset={relativePreset}
            isActiveFilter={isCurrentFilterActive}
            {...commonProps}
          />
        );
      }

      case "text":
      case "text_array":
      case "boolean":
      case "categorical": {
        const { values, mode } = getCategoricalFilterState(filterKey);
        // Exclusion (not_in) is only supported for values that serialize
        // as text filters on the backend.
        const supportsExclusion =
          dataType === "categorical" || dataType === "text";
        return (
          <FilterOptions
            key={filterKey}
            options={currentFilterOptions}
            selectedValues={values}
            onChange={(newValues) =>
              handleTempSelectionChange(filterKey, newValues, mode)
            }
            mode={mode}
            onModeChange={
              supportsExclusion
                ? (newMode) =>
                    handleTempSelectionChange(filterKey, values, newMode)
                : undefined
            }
            showApplyButton={true}
            onApply={() => handleApplyFilter()}
            onClose={handleClose}
          />
        );
      }

      default:
        return (
          <div className="p-4 text-center text-gray-500">
            <p>Unsupported filter type: {dataType}</p>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col w-[350px] h-full bg-white pl-5 pr-1 overflow-y-auto hide-scrollbar">
      {activeFilter ? (
        <>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-sm font-bold">
              {columnsTitleCreator
                ? columnsTitleCreator(activeFilter)
                : toTitleCase(activeFilter)}
            </h2>
          </div>
          {renderFilterComponent(activeFilter, currentDataType)}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-center text-[#556C75]">
          <FilterIcon className="mb-2 text-2xl" />
          <p className="text-sm">Select a filter to see its details here.</p>
        </div>
      )}
    </div>
  );
}
